import { useState } from 'react';
import PropTypes from 'prop-types';
import { UserX, AlertTriangle } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { disableUser } from '../../../services/userService';
import './UserManagement.css';

/**
 * ConfirmDisableModal Component
 * 
 * Confirmation modal for disabling a user (admin only)
 */
export default function ConfirmDisableModal({ isOpen, onClose, user, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle disable confirmation
  const handleConfirm = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');
      
      // Call API to disable user
      await disableUser(user.id);

      // Show success notification
      if (onSuccess) {
        onSuccess(`User ${user.name} has been disabled`);
      }

      // Close modal
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to disable user');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Disable User"
      size="sm"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={loading}
            icon={<UserX size={18} />}
          >
            Disable User
          </Button>
        </div>
      }
    >
      <div className="confirm-modal-content">
        <div className="warning-icon">
          <AlertTriangle size={48} />
        </div>
        
        <div className="confirm-message">
          <p className="confirm-text">
            Are you sure you want to disable <strong>{user.name}</strong>?
          </p>
          <p className="confirm-subtext">
            This user will no longer be able to access the application.
            You can re-enable them later if needed.
          </p>
        </div>

        <div className="user-details-box">
          <div className="detail-row">
            <span className="detail-label">Email:</span>
            <span className="detail-value">{user.email}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Role:</span>
            <span className="detail-value">{user.role}</span>
          </div>
        </div>

        {error && (
          <div className="form-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

ConfirmDisableModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired
  }),
  onSuccess: PropTypes.func
};
